import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Key, Loader2, AlertCircle, ArrowRight, ShieldCheck } from 'lucide-react';

export const Login: React.FC = () => {
  const navigate = useNavigate();

  const AUTH_KEY = 'chalet_inn_admin_auth';

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const setAuth = (value: boolean) => {
    try {
      if (value) localStorage.setItem(AUTH_KEY, 'true');
      else localStorage.removeItem(AUTH_KEY);
    } catch {
      // ignore storage errors (private mode etc.)
    }
  };

  useEffect(() => {
    // Prefer server session check (source of truth), fallback to localStorage for UX.
    (async () => {
      try {
        const res = await fetch('/api/auth/me.php', {
          credentials: 'include',
        });
        const j = await res.json().catch(() => ({}));

        if (j?.authenticated) {
          setAuth(true);
          navigate('/admin');
          return;
        }
      } catch {
        // ignore; fallback below
      }

      // Fallback: if localStorage says true, still try to go to /admin
      // (Admin.tsx will re-check session and show login if not valid)
      try {
        if (localStorage.getItem(AUTH_KEY) === 'true') {
          navigate('/admin');
        }
      } catch {
        // ignore
      }
    })();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoggingIn(true);

    try {
      // (Optional) keep a tiny delay for “professional feel”
      await new Promise((resolve) => setTimeout(resolve, 400));

      const res = await fetch('/api/auth/login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // IMPORTANT: send/receive session cookie
        body: JSON.stringify({ username, password }),
      });

      const j = await res.json().catch(() => ({}));

      if (!res.ok || !j?.success) {
        setAuth(false);
        setError(j?.error || 'Ongeldige inloggegevens. Controleer uw gebruikersnaam en wachtwoord.');
        setIsLoggingIn(false);
        return;
      }

      setAuth(true);
      navigate('/admin');
    } catch {
      setAuth(false);
      setError('Netwerkfout bij inloggen. Probeer opnieuw.');
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-50 px-6 py-12 animate-in fade-in duration-700">
      <div className="w-full max-w-md bg-white p-10 rounded-[3rem] shadow-2xl border border-brand-100 scale-in relative overflow-hidden">
        {/* Subtle decorative background element */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-50 rounded-full -translate-y-16 translate-x-16 -z-10"></div>

        <div className="flex flex-col items-center mb-10">
          <div className="w-16 h-16 bg-brand-900 rounded-2xl flex items-center justify-center shadow-xl mb-6 transform -rotate-3 hover:rotate-0 transition-transform duration-500">
            <ShieldCheck size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-serif font-bold text-brand-900 text-center tracking-tight">
            Inloggen
          </h1>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[9px] font-black text-stone-400 uppercase tracking-widest ml-2 flex items-center gap-2">
              <Lock size={10} /> Gebruikersnaam
            </label>
            <input
              type="text"
              placeholder="Gebruikersnaam"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full p-4 bg-brand-50/50 border border-brand-100 rounded-2xl focus:ring-1 focus:ring-brand-900 focus:bg-white outline-none transition-all font-bold placeholder:text-stone-300"
              autoComplete="username"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-[9px] font-black text-stone-400 uppercase tracking-widest ml-2 flex items-center gap-2">
              <Key size={10} /> Wachtwoord
            </label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-4 bg-brand-50/50 border border-brand-100 rounded-2xl focus:ring-1 focus:ring-brand-900 focus:bg-white outline-none transition-all font-bold placeholder:text-stone-300"
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-50 text-red-600 rounded-2xl border border-red-100 animate-in shake duration-300">
              <AlertCircle size={16} className="flex-shrink-0" />
              <p className="text-[10px] font-black uppercase tracking-tight leading-tight">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoggingIn}
            className="w-full py-5 mt-4 bg-brand-900 text-white rounded-2xl uppercase tracking-[0.2em] text-[11px] font-black hover:bg-black transition-all shadow-xl shadow-brand-900/10 transform active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-70"
          >
            {isLoggingIn ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <>
                Inloggen <ArrowRight size={14} />
              </>
            )}
          </button>
        </form>

        <div className="mt-10 pt-8 border-t border-brand-50 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-[9px] font-black text-stone-400 hover:text-brand-900 uppercase tracking-widest transition-colors"
          >
            Terug naar de website
          </button>
        </div>
      </div>
    </div>
  );
};
